import { useSelector, useDispatch } from 'react-redux';
import { NavLink, useLocation, Link } from 'react-router-dom';
import {
    toggleSidebarCollapse,
    setSidebarCollapsed,
    setMobileMenuOpen
} from '@store/slices/uiSlice';
import {
    LayoutDashboard,
    Building2,
    Home,
    Users,
    Building,
    Calendar,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    X,
    TrendingUp,
    Clock,
    Calculator,
    Receipt,
    DollarSign,
    ClipboardCheck,
    CalendarDays,
    Package,
    FileText,
    BarChart2,
    ArrowDownCircle,
    ArrowUpCircle,
    Camera,
    Hammer,
    Wrench,
    Paintbrush,
    Boxes,
    Sofa,
    ArrowUpDown,
    Wind,
    Scale,
} from 'lucide-react';
import clsx from 'clsx';
import { useEffect, useState, useMemo } from 'react';

// Finans modülü menü yapısı
const finansMenuItems = [
    { path: '/finans/ozet', label: 'Hızlı Bakış', icon: LayoutDashboard },
    {
        type: 'divider',
        label: 'Projeler'
    },
    { path: '/finans/projeler', label: 'Projeler', icon: Building2 },
    { path: '/finans/daireler', label: 'Daireler', icon: Home },
    {
        type: 'divider',
        label: 'Finansal'
    },
    {
        type: 'group',
        id: 'gelirler',
        label: 'Gelirler',
        icon: ArrowDownCircle,
        color: 'text-success',
        children: [
            { path: '/finans/satislar', label: 'Satışlar', icon: DollarSign },
            { path: '/finans/taksitler', label: 'Taksit Takibi', icon: Calendar },
        ]
    },
    {
        type: 'group',
        id: 'giderler',
        label: 'Giderler',
        icon: ArrowUpCircle,
        color: 'text-error',
        children: [
            { path: '/finans/harcamalar', label: 'Harcamalar', icon: Receipt },
            { path: '/finans/planli-odemeler', label: 'Ödemeler & Borçlar', icon: Clock },
            { path: '/finans/cari', label: 'Cari Hesaplar', icon: Scale },
        ]
    },
    { path: '/finans/cekler', label: 'Çek Takibi', icon: FileText },
    {
        type: 'group',
        id: 'raporlar',
        label: 'Raporlar',
        icon: BarChart2,
        color: 'text-tertiary',
        children: [
            { path: '/finans/raporlar', label: 'Mali Özet', icon: TrendingUp },
            { path: '/finans/maliyet-tahmini', label: 'Maliyet Tahmini', icon: Calculator },
        ]
    },
    {
        type: 'divider',
        label: 'Kişiler & Firmalar'
    },
    { path: '/finans/musteriler', label: 'Müşteriler', icon: Users },
    { path: '/finans/firmalar', label: 'Firmalar', icon: Building },
    {
        type: 'divider',
        label: 'Planlama'
    },
    { path: '/finans/program', label: 'Program Defteri', icon: CalendarDays },
];

// İnşaat modülü menü yapısı
const insaatMenuItems = [
    { path: '/insaat/ozet', label: 'Hızlı Bakış', icon: LayoutDashboard },
    {
        type: 'divider',
        label: 'Projeler'
    },
    { path: '/insaat/projeler', label: 'Projeler', icon: Building2 },
    {
        type: 'divider',
        label: 'Malzeme & Yapım Takibi'
    },
    {
        type: 'group',
        id: 'kaba',
        label: 'Kaba İnşaat',
        icon: Hammer,
        color: 'text-neutral-400',
        children: [
            { path: '/insaat/kaba/beton', label: 'Beton & Kalıp', icon: Package },
            { path: '/insaat/kaba/demir', label: 'Demir & Çelik', icon: Package },
            { path: '/insaat/kaba/tugla', label: 'Tuğla & Duvar', icon: Package },
            { path: '/insaat/kaba/izolasyon', label: 'İzolasyon', icon: Package },
            { path: '/insaat/kaba/fore-kazik', label: 'Fore Kazık', icon: Package },
            { path: '/insaat/kaba/cati', label: 'Çatı & Teras', icon: Package },
        ]
    },
    {
        type: 'group',
        id: 'tesisat',
        label: 'Tesisat & Mekanik',
        icon: Wrench,
        color: 'text-info',
        children: [
            { path: '/insaat/tesisat/elektrik', label: 'Elektrik', icon: Package },
            { path: '/insaat/tesisat/su-tesisati', label: 'Su Tesisatı', icon: Package },
            { path: '/insaat/tesisat/dogalgaz', label: 'Doğalgaz & Isıtma', icon: Package },
            { path: '/insaat/tesisat/asansor', label: 'Asansör', icon: ArrowUpDown },
            { path: '/insaat/tesisat/havalandirma', label: 'Havalandırma & Klima', icon: Wind },
        ]
    },
    {
        type: 'group',
        id: 'ince',
        label: 'İnce İşler & Kaplama',
        icon: Paintbrush,
        color: 'text-secondary',
        children: [
            { path: '/insaat/ince/alci-siva', label: 'Sıva & Alçı', icon: Package },
            { path: '/insaat/ince/seramik', label: 'Seramik & Fayans', icon: Package },
            { path: '/insaat/ince/boya', label: 'Boya & Badana', icon: Package },
            { path: '/insaat/ince/parke', label: 'Parke & Zemin', icon: Package },
            { path: '/insaat/ince/kapi-pencere', label: 'Kapı & Pencere', icon: Package },
        ]
    },
    {
        type: 'group',
        id: 'icmekan',
        label: 'İç Mekan & Mobilya',
        icon: Sofa,
        color: 'text-warning',
        children: [
            { path: '/insaat/icmekan/mutfak', label: 'Mutfak', icon: Package },
            { path: '/insaat/icmekan/banyo', label: 'Banyo & WC', icon: Package },
            { path: '/insaat/icmekan/gardrop', label: 'Gardırop & Dolaplar', icon: Package },
        ]
    },
    {
        type: 'divider',
        label: 'Operasyon'
    },
    { path: '/insaat/personel', label: 'Personel & Yoklama', icon: Users },
    { path: '/insaat/gunluk', label: 'Şantiye Günlüğü', icon: Camera },
];

// Tüm grup child path'lerini çıkar
const getGroupPaths = (menuItems) => {
    const paths = {};
    menuItems.forEach(item => {
        if (item.type === 'group' && item.children) {
            item.children.forEach(child => {
                paths[child.path] = item.id;
            });
        }
    });
    return paths;
};

export default function Sidebar() {
    const dispatch = useDispatch();
    const location = useLocation();
    const { sidebarCollapsed, mobileMenuOpen, activeModule } = useSelector((state) => state.ui);
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const { role } = useSelector((state) => state.auth);

    // Aktif modüle ve role göre menü öğelerini seç.
    // Şef (sef) rolü: Finans modülüne erişimi yoktur — her zaman insaat menüsü gösterilir.
    const menuItems = (activeModule === 'finans' && role === 'admin') ? finansMenuItems : insaatMenuItems;

    // Collapsible grup state'leri
    const [openGroups, setOpenGroups] = useState(['gelirler']);

    const groupPaths = useMemo(() => getGroupPaths(menuItems), [menuItems]);

    // Aktif sayfanın grubu varsa otomatik aç
    useEffect(() => {
        const groupId = groupPaths[location.pathname];
        if (groupId && !openGroups.includes(groupId)) {
            setOpenGroups(prev => [...prev, groupId]);
        }
    }, [location.pathname, groupPaths]);

    // Handle screen resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                dispatch(setSidebarCollapsed(true));
                dispatch(setMobileMenuOpen(false));
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [dispatch]);

    // Close mobile menu on route change
    useEffect(() => {
        dispatch(setMobileMenuOpen(false));
    }, [location.pathname, dispatch]);

    const handleToggle = () => {
        dispatch(toggleSidebarCollapse());
    };

    const closeMobileMenu = () => {
        dispatch(setMobileMenuOpen(false));
    };

    const toggleGroup = (groupId) => {
        setOpenGroups(prev =>
            prev.includes(groupId)
                ? prev.filter(g => g !== groupId)
                : [...prev, groupId]
        );
    };

    // Grup içindeki herhangi bir sayfa aktif mi?
    const isGroupActive = (children) => {
        return children?.some(child => location.pathname === child.path || location.pathname.startsWith(child.path + '/'));
    };

    const renderMenuItem = (item, isMobile = false) => {
        // Divider
        if (item.type === 'divider') {
            if (sidebarCollapsed && !isMobile) {
                return <hr key={item.label} className="my-3 border-primary-600" />;
            }
            return (
                <div key={item.label} className="px-4 py-2 mt-4 mb-1">
                    <span className="text-[10px] font-bold text-primary-400 uppercase tracking-widest">
                        {item.label}
                    </span>
                </div>
            );
        }

        // Collapsible Group
        if (item.type === 'group') {
            const isOpen = openGroups.includes(item.id);
            const hasActiveChild = isGroupActive(item.children);
            const Icon = item.icon;

            // Collapsed sidebar - inline genişleme (sadece ikonlar)
            if (sidebarCollapsed && !isMobile) {
                return (
                    <div key={item.id} className="mb-1">
                        {/* Grup başlık butonu */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleGroup(item.id);
                            }}
                            className={clsx(
                                'flex items-center justify-center w-full p-2.5 rounded-lg transition-all relative',
                                hasActiveChild
                                    ? 'bg-primary text-white'
                                    : isOpen
                                        ? 'bg-primary-600 text-white'
                                        : 'text-primary-200 hover:bg-primary hover:text-white'
                            )}
                            title={item.label}
                        >
                            <Icon size={20} className={item.color} />
                            {/* Alt ok ikonu - açık/kapalı durumu gösterir */}
                            <ChevronDown
                                size={10}
                                className={clsx(
                                    'absolute right-1 bottom-1 transition-transform',
                                    isOpen ? 'rotate-180' : ''
                                )}
                            />
                        </button>

                        {/* Alt öğeler - sidebar içinde aşağı doğru sadece ikon olarak */}
                        {isOpen && (
                            <div className="mt-1 space-y-1 bg-white/10 rounded-lg p-1">
                                {item.children.map(child => {
                                    const ChildIcon = child.icon;
                                    return (
                                        <NavLink
                                            key={child.path}
                                            to={child.path}
                                            className={({ isActive }) =>
                                                clsx(
                                                    'flex items-center justify-center w-full p-2 rounded-lg transition-all',
                                                    isActive
                                                        ? 'bg-white text-primary'
                                                        : 'text-primary-200 hover:bg-white/20 hover:text-white'
                                                )
                                            }
                                            title={child.label}
                                        >
                                            <ChildIcon size={18} className={child.color} />
                                        </NavLink>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            }

            // Expanded sidebar - collapsible grup
            return (
                <div key={item.id} className="mb-1">
                    <button
                        onClick={() => toggleGroup(item.id)}
                        className={clsx(
                            'flex items-center justify-between w-full px-4 py-2.5 rounded-lg transition-all',
                            hasActiveChild
                                ? 'bg-primary/50 text-white'
                                : 'text-primary-200 hover:bg-primary/30 hover:text-white'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <Icon size={20} className={item.color} />
                            <span className="font-medium text-sm">{item.label}</span>
                        </div>
                        <ChevronDown
                            size={16}
                            className={clsx(
                                'transition-transform duration-200',
                                isOpen ? 'rotate-180' : ''
                            )}
                        />
                    </button>

                    {/* Alt menü - animasyonlu */}
                    <div
                        className={clsx(
                            'overflow-hidden transition-all duration-200',
                            isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                        )}
                    >
                        <div className="ml-4 mt-1 pl-4 border-l-2 border-primary-600">
                            {item.children.map(child => {
                                const ChildIcon = child.icon;
                                return (
                                    <NavLink
                                        key={child.path}
                                        to={child.path}
                                        onClick={isMobile ? closeMobileMenu : undefined}
                                        className={({ isActive }) =>
                                            clsx(
                                                'flex items-center gap-3 px-3 py-2 rounded-lg mb-0.5 transition-all text-sm',
                                                isActive
                                                    ? 'bg-secondary text-white shadow-lg shadow-secondary/20'
                                                    : 'text-primary-300 hover:bg-primary/40 hover:text-white'
                                            )
                                        }
                                    >
                                        <ChildIcon size={16} />
                                        <span>{child.label}</span>
                                    </NavLink>
                                );
                            })}
                        </div>
                    </div>
                </div>
            );
        }

        // Normal menu item
        const Icon = item.icon;
        return (
            <NavLink
                key={item.path}
                to={item.path}
                onClick={isMobile ? closeMobileMenu : undefined}
                className={({ isActive }) =>
                    clsx(
                        'flex items-center gap-3 px-4 py-2.5 rounded-lg mb-1 transition-all duration-200',
                        sidebarCollapsed && !isMobile && 'justify-center px-2',
                        isActive
                            ? 'bg-secondary text-white shadow-lg shadow-secondary/30'
                            : 'text-primary-200 hover:bg-primary hover:text-white'
                    )
                }
                title={sidebarCollapsed && !isMobile ? item.label : undefined}
            >
                <Icon size={20} />
                {(!sidebarCollapsed || isMobile) && <span className="font-medium text-sm">{item.label}</span>}
            </NavLink>
        );
    };

    // Modül etiketi (Finans veya İnşaat)
    const moduleLabel = activeModule === 'finans' ? 'Finans Modülü' : 'İnşaat Modülü';

    return (
        <>
            {/* Mobile Overlay */}
            {mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={closeMobileMenu}
                />
            )}

            {/* Desktop Sidebar */}
            <aside
                className={clsx(
                    'fixed left-0 top-0 h-screen bg-primary-dark text-white z-50 transition-all duration-300',
                    'flex flex-col',
                    'hidden md:flex',
                    sidebarCollapsed ? 'w-[var(--sidebar-collapsed-width)]' : 'w-[var(--sidebar-width)]',
                )}
            >
                {/* Logo */}
                <div className="flex items-center justify-between h-16 px-3 border-b border-primary-600">
                    <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
                        <img src="/logo.png" alt="İNŞA ET" className="h-9 w-9 object-contain" />
                        {!sidebarCollapsed && (
                            <span className="text-lg font-bold whitespace-nowrap">
                                <span className="text-white">İNŞA</span>
                                <span className="text-secondary">ET</span>
                            </span>
                        )}
                    </Link>

                    <button
                        onClick={handleToggle}
                        className="p-2 rounded-lg hover:bg-primary transition-colors cursor-pointer"
                    >
                        {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                    </button>
                </div>

                {/* Selected Construction Info */}
                {!sidebarCollapsed && (
                    <div className="px-4 py-3 border-b border-primary-600 bg-primary">
                        <p className="text-sm font-medium truncate">
                            {showAllConstructions ? 'Tüm İnşaatlar' : selectedConstruction?.name || 'Seçim yapılmadı'}
                        </p>
                        <p className="text-[10px] text-primary-400 mt-1">{moduleLabel}</p>
                    </div>
                )}

                {/* Navigation */}
                <nav className="flex-1 mt-2 px-2 overflow-y-auto scrollbar-thin scrollbar-thumb-primary-600">
                    {menuItems.map((item) => renderMenuItem(item, false))}
                </nav>
            </aside>

            {/* Mobile Sidebar */}
            <aside
                className={clsx(
                    'fixed left-0 top-0 h-screen bg-primary-dark text-white z-50 transition-transform duration-300',
                    'flex flex-col w-[var(--sidebar-width)]',
                    'md:hidden',
                    mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                {/* Mobile Header */}
                <div className="flex items-center justify-between h-16 px-4 border-b border-primary-600">
                    <Link to="/" onClick={closeMobileMenu} className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
                        <img src="/logo.png" alt="İNŞA ET" className="h-8 w-8 object-contain" />
                        <span className="text-xl font-bold">
                            <span className="text-white">İNŞA</span>
                            <span className="text-secondary">ET</span>
                        </span>
                    </Link>
                    <button
                        onClick={closeMobileMenu}
                        className="p-2 rounded-lg hover:bg-primary transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Mobile Construction Info */}
                <div className="px-4 py-3 border-b border-primary-600 bg-primary">
                    <p className="text-sm font-medium truncate">
                        {showAllConstructions ? 'Tüm İnşaatlar' : selectedConstruction?.name || 'Seçim yapılmadı'}
                    </p>
                    <p className="text-[10px] text-primary-400 mt-1">{moduleLabel}</p>
                </div>

                {/* Mobile Navigation */}
                <nav className="flex-1 mt-2 px-2 overflow-y-auto">
                    {menuItems.map((item) => renderMenuItem(item, true))}
                </nav>
            </aside>
        </>
    );
}
